<template>
  <span data-test-id="statusChip">
    <div style="display: inline-flex" v-if="mini">
      <v-icon
        v-if="isoDate"
        v-tooltip="{ openOnClick: true, disabled: !isoDate, text: getToolTipText(isoDate) }"
        style="font-size: 1rem; margin-left: 0.25rem"
        :color="getStatusColor(isoDate)"
        >{{ getStatusIcon(isoDate) }}</v-icon
      >
    </div>
    <div v-else>
      <span class="chip-label" v-if="label">{{ label }}:</span>
      <v-chip
        v-tooltip="{ openOnClick: true, disabled: !isoDate, text: getToolTipText(isoDate) }"
        v-if="!disabled"
        :color="getStatusColor(isoDate)"
        size="small"
        density="compact"
        :prepend-icon="getSchedulingIcon(isoDate)"
      >
        {{ getStatusMessage(isoDate) }}
      </v-chip>
      <span v-else style="margin-left: 0.25rem">
        {{ _defaultValue }}
      </span>
    </div>
  </span>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { formatDate, isDefined } from '@/composables/utils'
import Colors from '@/styles/_colors.module.scss'
import dayjs from 'dayjs'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({ useScope: 'global' })

// PROPS & EMITS
const props = defineProps({
  daysUntilDue: { type: Number, default: 30 },
  daysToExpire: { type: Number, default: 0 },
  defaultValue: { type: String },
  disabled: { type: Boolean, default: false },
  isoDate: { type: String },
  label: { type: String },
  msgDueSoon: { type: String },
  msgOverdue: { type: String },
  msgCompliant: { type: String },
  msgNoRecord: { type: String },
  expires: { type: Boolean, default: true },
  mini: { type: Boolean, default: false },
  scheduled: { type: Boolean, default: false }
})

// REACTIVE VARIABLES
const _defaultValue = ref<string>(t('labels.notApplicable'))

// COMPUTED PROPERTIES

// LIFECYCLE HOOKS

// WATCHERS
watch(
  (): any => props.defaultValue,
  (value) => {
    _defaultValue.value = value
  }
)

// FUNCTIONS
const getSchedulingIcon = (isDate?: string) => {
  // No scheduling icon if the category does not expire.
  if (!props.expires) {
    return ''
  }
  if (isDate && (isDueSoon(isDate) || isOverdue(isDate))) {
    return props.scheduled ? 'mdi-calendar' : 'mdi-alert-circle'
  }
  return ''
}

const getStatusColor = (isoDate?: string) => {
  if (isoDate) {
    isoDate = dayjs(isoDate).add(props.daysToExpire, 'day').toISOString()
  }

  if (!isDefined(isoDate)) {
    return Colors.chartNoRecord
  }
  if (isDueSoon(isoDate!) && props.expires) {
    if (props.scheduled) {
      return Colors.chartDueSoonScheduled
    }
    return Colors.chartDueSoonNotScheduled
  }
  if (isOverdue(isoDate!) && props.expires) {
    if (props.scheduled) {
      return Colors.chartOverdueScheduled
    }
    return Colors.chartOverdueNotScheduled
  }
  return Colors.chartCompliant
}

const getStatusIcon = (isoDate?: string) => {
  if (isoDate) {
    isoDate = dayjs(isoDate).add(props.daysToExpire, 'day').toISOString()
  }
  if (isDueSoon(isoDate!) && props.expires) {
    return 'mdi-alert'
  }
  if (isOverdue(isoDate!) && props.expires) {
    return 'mdi-alert-circle'
  }
  return 'mdi-check-circle'
}

const getStatusMessage = (isoDate?: string) => {
  if (isoDate) {
    isoDate = dayjs(isoDate).add(props.daysToExpire, 'day').toISOString()
  }
  const msgDueSoon = isDefined(props.msgDueSoon) ? props.msgDueSoon : t('labels.dueSoon')
  const msgOverdue = isDefined(props.msgOverdue) ? props.msgOverdue : t('labels.overdue')
  const msgCompliant = isDefined(props.msgCompliant) ? props.msgCompliant : t('labels.compliant')
  const msgNoRecord = isDefined(props.msgNoRecord) ? props.msgNoRecord : t('labels.noRecord')
  if (!isoDate) {
    return msgNoRecord
  }
  if (props.expires && isDueSoon(isoDate)) {
    return msgDueSoon
  }
  if (props.expires && isOverdue(isoDate)) {
    return msgOverdue
  }
  return msgCompliant
}

const getToolTipText = (isoDate?: string) => {
  if (!isoDate) {
    return ''
  }

  if (isoDate) {
    isoDate = dayjs(isoDate).add(props.daysToExpire, 'day').toISOString()
  }

  if (props.expires && isDueSoon(isoDate)) {
    const today = dayjs().format('YYYY-MM-DD')
    const date = dayjs(isoDate).format('YYYY-MM-DD')
    const days = dayjs(date).diff(dayjs(today), 'day')

    let dueSoonMessage = t('labels.daysToExpire', { count: days })
    if (props.scheduled) {
      dueSoonMessage = `${dueSoonMessage} (${t('labels.scheduled')})`
    } else {
      dueSoonMessage = `${dueSoonMessage} (${t('labels.notScheduled')})`
    }
    return dueSoonMessage
  }

  if (props.expires && isOverdue(isoDate)) {
    const days = dayjs(new Date()).diff(isoDate, 'day')
    let overdueMessage = t('labels.daysOverdue', days)
    if (props.scheduled) {
      overdueMessage = `${overdueMessage} (${t('labels.scheduled')})`
    } else {
      overdueMessage = `${overdueMessage} (${t('labels.notScheduled')})`
    }
    return overdueMessage
  }

  if (props.mini) {
    return isDefined(props.msgCompliant) ? props.msgCompliant : t('labels.compliant')
  }

  return formatDate(isoDate)
}

const isDueSoon = (value: string) => {
  const today = dayjs(dayjs().format('MM/DD/YYYY')).toDate()
  const futureDate = dayjs(today).add(props.daysUntilDue, 'day').toDate()
  const testDueDate = dayjs(value).toDate()
  return testDueDate >= today && testDueDate <= futureDate
}

const isOverdue = (isoDate: string) => {
  const today = dayjs().toDate()
  const testDueDate = dayjs(isoDate).toDate()
  return testDueDate < today
}
</script>

<style lang="scss" scoped>
.chip-label {
  font-size: 0.75rem;
  margin-right: 0.5rem;
}
</style>
